<template>
  <b-card v-loading="loading">
    <h4>
      <img src="/img/publishers/kijiji.png" height="35" alt="Kijiji" />
      <div class="pull-right">
        <c-switch :default-checked="kijiji.active" @change="(val) => kijiji.active = val" :label="true" color="success" variant="pill" :disabled="!hasKijijiAccount" v-b-tooltip.hover :title="kijiji.active ? 'This listing will be posted to Kijiji' : 'This listing will NOT be posted to Kijiji'" />
      </div>
    </h4>
    <b-alert :show="!hasKijijiAccount" variant="warning" class="mt-2 mb-0 py-2 px-3 d-flex align-items-center">
      <div><i class="icon-info mr-2"></i> You cannot activate Kijiji for this listing as you have not setup your account. Please <b-link to="/publishers">visit the publishers</b-link> page to get started.</div>
    </b-alert>
    <b-collapse :visible="hasKijijiAccount && kijiji.active" id="collapseKijiji" class="mt-3">
      <b-alert :show="!!error" variant="danger" class="mt-2 mb-0 py-2 px-3 d-flex align-items-center">
        <i class="icon-info mr-2"></i> {{error}}
      </b-alert>
      <b-form>
        <b-form-group>
          <label>Full Address</label>
          <b-input-group>
            <vue-google-autocomplete id="kijiji-address" ref="kijijiAddress" classname="form-control" placeholder="Start typing your street address..." :enable-geolocation="true" country="ca" @placechanged="addressChanged" />
            <b-input-group-append is-text slot="append">
              <b-form-checkbox v-model="kijiji.addressExact" :value="true" style="margin-right: 0" v-b-tooltip.hover title="Precise location will show your exact address (including the street number) on the map">Precise</b-form-checkbox>
            </b-input-group-append>
          </b-input-group>
          <!-- @TODO improve with better styling -->
          <!-- @TODO https://www.kijiji.ca/p-location-id?categoryId=692&lat=43.8576686&lon=-79.44630860000001 -->
        </b-form-group>
        <b-row>
          <b-col :md="6">
            <b-form-group v-loading="categories.loading">
              <label>Category</label>
              <vue-treeselect v-model="kijiji.categoryID" :searchable="false" :disable-branch-nodes="true" :options="categories.results" />
            </b-form-group>
          </b-col>
          <b-col :md="6">
            <b-form-group v-loading="locations.loading">
              <label>Location</label>
              <vue-treeselect v-model="kijiji.locationID" :searchable="false" :disable-branch-nodes="true" :options="locations.results" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col :md="6">
            <b-form-group>
              <label>Ad Type</label>
              <b-form-radio-group v-model="kijiji.adType" class="d-block" buttons button-variant="outline-primary" :options="[{ text: 'OFFERING', value: 'OFFER' }, { text: 'WANTED', value: 'WANTED'}]" />
            </b-form-group>
          </b-col>
          <b-col :md="6">
            <b-form-group>
              <label>For Sale By</label>
              <b-form-radio-group v-model="kijiji.saleBy" class="d-block" buttons button-variant="outline-primary" :options="[{ text: 'Owner', value: 'ownr' }, { text: 'Business', value: 'delr'}]" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group>
          <label>Price Type</label>
          <b-form-radio-group v-model="kijiji.priceType" class="d-block" buttons button-variant="outline-primary" :options="[{ text: 'Fixed', value: 'FIXED' }, { text: 'Free', value: 'GIVE_AWAY'}, { text: 'Please Contact', value: 'CONTACT'}, { text: 'Swap / Trade', value: 'SWAP_TRADE'}]" />
          <div v-if="kijiji.priceType === 'FIXED'" class="input-group mt-2">
            <div class="input-group-prepend">
              <span class="input-group-text">$</span>
            </div>
            <b-form-input v-model="kijiji.price" type="text" class="w-200px" />
          </div>
        </b-form-group>
        <b-form-group>
          <label>Phone Number <em>(optional)</em></label>
          <b-form-input v-model="kijiji.phoneNumber" type="text" placeholder="ex. (123) 456-7890" maxlength="40" />
        </b-form-group>
      </b-form>
    </b-collapse>
  </b-card>
</template>

<script>
import { Switch as cSwitch } from '@coreui/vue'
import VueTreeselect from '@riophae/vue-treeselect'
import VueGoogleAutocomplete from 'vue-google-autocomplete'

export default {
  name: 'Kijiji',

  components: {
    cSwitch,
    VueTreeselect,
    VueGoogleAutocomplete
  },

  props: {
    kijiji: {
      type: Object,
      required: true,
      default(){
        return {
          active      : true,
          address     : '',
          adType      : 'OFFER',
          saleBy      : 'ownr',
          priceType   : 'FIXED',
          price       : '',
          phoneNumber : '',
          locationID  : null,
          categoryID  : null,
        }
      }
    },
    loading: {
      type    : Boolean,
      default : false,
    }
  },

  data(){
    const hasKijijiAccount = this.$session.get("publishers.kijiji") === true

    return {
      locations: {
        results: [],
        loading : false,
      },
      categories: {
        results: [],
        loading : false,
      },

      // State
      hasKijijiAccount,
      error : '',
    }
  },

  mounted(){
    // @TODO only load if we have kijiji enabled
    this.loadKijijiLocations()
    this.loadKijijiCategories()

    this.$refs.kijijiAddress.update(this.kijiji.address)
  },

  watch: {
    'kijiji.address'(){
      this.$refs.kijijiAddress.update(this.kijiji.address)
    }
  },

  methods: {
    loadKijijiLocations(){
      this.locations.loading = true
      this.$API.v2.Publishers.Kijiji.ListLocations()
      .then((locations) => {
        this.locations.results = locations.children
      })
      .catch((err) => {
        this.error = err
      })
      .then(() => {
        this.locations.loading = false
      })
    },

    loadKijijiCategories(){
      this.categories.loading = true
      this.$API.v2.Publishers.Kijiji.ListCategories()
      .then((categories) => {
        this.categories.results = categories
      })
      .catch((err) => {
        this.error = err
      })
      .then(() => {
        this.categories.loading = false
      })
    },

    addressChanged(addressData, { formatted_address }) {
      // @TODO submit Maps data (postal code, lat, long, etc.)
      this.kijiji.address = formatted_address
    }
  }
};
</script>

<style lang="scss" scoped>
.w-200px {
  width: 200px;
  flex: 0;
}
</style>
